import axiosWithAuth from '@/utils/axiosWithAuth';
import {HTTP_STATUS} from '@/config/constants';

const CREATORS_PATH = 'creators';

const creatorsUpdaters = {
  startConversation: async (data: {
    brandId: string;
    creatorId: string;
    initialMessage?: string;
  }): Promise<StartConversationResponse> => {
    const result = await axiosWithAuth.post<{data: {id: string}}>(
      `${CREATORS_PATH}/conversation`,
      {
        brand_id: data.brandId,
        creator_id: data.creatorId,
        ...(data.initialMessage && {initial_message: data.initialMessage}),
      }
    );
    return result.data.data;
  },
  addToLists: async (data: {
    creatorGroupId: string;
    creatorIds: string[];
    labelIds: string[];
  }) => {
    const result = await axiosWithAuth.put(`${CREATORS_PATH}/creator_list`, {
      creator_group_id: data.creatorGroupId,
      creator_ids: data.creatorIds,
      label_ids: data.labelIds,
    });
    return (
      result.status >= HTTP_STATUS.OK && result.status < HTTP_STATUS.BAD_REQUEST
    );
  },
  addToCampaign: async (data: {
    creatorGroupId: string;
    creatorIds: string[];
    campaignId: string;
  }) => {
    const result = await axiosWithAuth.put(
      `${CREATORS_PATH}/campaign_invitation`,
      {
        creator_group_id: data.creatorGroupId,
        creator_ids: data.creatorIds,
        campaign_id: data.campaignId,
      }
    );
    return (
      result.status >= HTTP_STATUS.OK && result.status < HTTP_STATUS.BAD_REQUEST
    );
  },
  recruitToCrm: async (data: {
    agencyId: string;
    creatorIds?: string[];
    emails?: string[];
  }) => {
    const result = await axiosWithAuth.post(
      `agencies/${data.agencyId}/crm/recruitments/bulk`,
      {
        ...(data.creatorIds?.length && {profile_ids: data.creatorIds}),
        ...(data.emails?.length && {emails: data.emails}),
      }
    );
    const {results, summary} = result.data as RawRecruitToCrmResponse;
    const mapProfile = (p: RawRecruitmentProfile) => ({
      profileId: p.profile_id,
      success: p.success,
      recruitmentId: p.recruitment_id,
      status: p.status,
      errorCode: p.error_code,
    });
    const mapEmail = (e: RawRecruitmentEmail) => ({
      email: e.email,
      success: e.success,
      recruitmentId: e.recruitment_id,
      status: e.status,
      errorCode: e.error_code,
    });
    return {
      results: {
        profiles: results.profiles.map(mapProfile),
        emails: results.emails.map(mapEmail),
      },
      summary,
    };
  },
  bulkThreads: async (data: {
    agencyId: string;
    brandId: string;
    creatorIds: string[];
    initialMessage: string;
  }) => {
    const result = await axiosWithAuth.post(
      `agencies/${data.agencyId}/crm/bulk_threads`,
      {
        profile_ids: data.creatorIds,
        brand_id: data.brandId,
        initial_message: data.initialMessage,
      }
    );
    return result.data; // {job_id, total}
  },
  bulkEmails: async (data: {
    agencyId: string;
    creatorIds: string[];
    subject: string;
    body: string;
    template?: string;
  }) => {
    const result = await axiosWithAuth.post(
      `agencies/${data.agencyId}/crm/bulk_emails`,
      {
        profile_ids: data.creatorIds,
        subject: data.subject,
        body: data.body,
        ...(data.template && {template: data.template}),
      }
    );
    return result.data; // {message, recipient_count}
  },
  addToCampaignAll: async (data: {
    agencyId: string;
    searchSessionId: string;
    campaignId: string;
    personalNote?: string;
  }): Promise<BulkEnqueuedResponse> => {
    const result = await axiosWithAuth.post<BulkEnqueuedResponse>(
      `agencies/${data.agencyId}/crm/bulk_invite_all`,
      {
        search_session_id: data.searchSessionId,
        campaign_id: data.campaignId,
        ...(data.personalNote && {personal_note: data.personalNote}),
      }
    );
    return result.data;
  },
  sendCampaignInvites: async (data: {
    agencyId: string;
    campaignId: string;
    profileIds: string[];
    offPlatformCreatorIds: string[];
    personalNote?: string;
  }): Promise<CampaignInvitesResponse> => {
    const result = await axiosWithAuth.post<CampaignInvitesApiResponse>(
      `agencies/${data.agencyId}/crm/campaign_invites`,
      {
        campaign_id: data.campaignId,
        profile_ids: data.profileIds,
        off_platform_creator_ids: data.offPlatformCreatorIds,
        ...(data.personalNote && {personal_note: data.personalNote}),
      }
    );
    const {onPlatform, offPlatform} = result.data;
    return {
      onPlatformInvited: onPlatform.invited,
      onPlatformFailed: onPlatform.failed ?? 0,
      offPlatformInvited: offPlatform.invited ?? 0,
      offPlatformNowOnPlatform: offPlatform.nowOnPlatform ?? 0,
      offPlatformEnqueued: offPlatform.enqueued ?? 0,
      offPlatformFailed: offPlatform.failed ?? 0,
      suppressed: offPlatform.skipped?.suppressed ?? 0,
      alreadyInvited: offPlatform.skipped?.alreadyInvited ?? 0,
    };
  },
  addToListsAll: async (data: {
    agencyId: string;
    searchSessionId: string;
    creatorGroupId: string;
    labelIds: string[];
  }): Promise<BulkEnqueuedResponse> => {
    const result = await axiosWithAuth.post<BulkEnqueuedResponse>(
      `agencies/${data.agencyId}/crm/bulk_add_to_list_all`,
      {
        search_session_id: data.searchSessionId,
        creator_group_id: data.creatorGroupId,
        label_ids: data.labelIds,
      }
    );
    return result.data;
  },
  bulkEmailsAll: async (data: {
    agencyId: string;
    searchSessionId: string;
    subject: string;
    body: string;
  }): Promise<BulkEnqueuedResponse> => {
    const result = await axiosWithAuth.post(
      `agencies/${data.agencyId}/crm/bulk_emails_all`,
      {
        search_session_id: data.searchSessionId,
        subject: data.subject,
        body: data.body,
      }
    );
    return result.data;
  },
  createCollaborationConversation: async (data: {
    creatorId: string;
    brandId: string;
  }): Promise<{id: string}> => {
    const result = await axiosWithAuth.post<{data: {id: string}}>(
      `${CREATORS_PATH}/conversation`,
      {creator_id: data.creatorId, brand_id: data.brandId}
    );
    return result.data.data;
  },
  bulkThreadsAll: async (data: {
    agencyId: string;
    brandId: string;
    searchSessionId: string;
    initialMessage: string;
  }): Promise<BulkEnqueuedResponse> => {
    const result = await axiosWithAuth.post(
      `agencies/${data.agencyId}/crm/bulk_threads_all`,
      {
        search_session_id: data.searchSessionId,
        brand_id: data.brandId,
        initial_message: data.initialMessage,
      }
    );
    return result.data;
  },
  recruitAllToCrm: async (data: {
    agencyId: string;
    searchSessionId: string;
  }): Promise<BulkEnqueuedResponse> => {
    const result = await axiosWithAuth.post<BulkEnqueuedResponse>(
      `agencies/${data.agencyId}/crm/recruitments/recruit_all`,
      {
        search_session_id: data.searchSessionId,
      }
    );
    return result.data;
  },
};

export type StartConversationResponse = {id: string};

export type BulkEnqueuedResponse = {status: string};

type CampaignInvitesApiResponse = {
  onPlatform: {invited: number; failed?: number};
  offPlatform: {
    invited?: number;
    nowOnPlatform?: number;
    enqueued?: number;
    failed?: number;
    skipped?: {suppressed: number; alreadyInvited: number};
  };
};

export type CampaignInvitesResponse = {
  onPlatformInvited: number;
  onPlatformFailed: number;
  offPlatformInvited: number;
  offPlatformNowOnPlatform: number;
  offPlatformEnqueued: number;
  offPlatformFailed: number;
  suppressed: number;
  alreadyInvited: number;
};

export default creatorsUpdaters;

type RawRecruitmentProfile = {
  profile_id: string;
  success: boolean;
  recruitment_id?: string;
  status?: string;
  error_code?: string;
};

type RawRecruitmentEmail = {
  email: string;
  success: boolean;
  recruitment_id?: string;
  status?: string;
  error_code?: string;
};

type RawRecruitToCrmResponse = {
  results: {
    profiles: RawRecruitmentProfile[];
    emails: RawRecruitmentEmail[];
  };
  summary: {
    total: number;
    succeeded: number;
    failed: number;
  };
};
